import { useQuery } from "@tanstack/react-query";
import { fetchPersonas, type PersonaSummary } from "../../api/personas";
import { Sheet } from "../../components/Sheet";

interface PersonaPickerProps {
  open: boolean;
  currentPersonaId: string | null;
  onSelect: (personaId: string | null) => void;
  onClose: () => void;
}

export function PersonaPicker({
  open,
  currentPersonaId,
  onSelect,
  onClose,
}: PersonaPickerProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["personas"],
    queryFn: fetchPersonas,
    enabled: open,
  });

  const activePersonas: PersonaSummary[] =
    data?.items.filter((p) => p.state === "active") ?? [];

  function getInitials(name: string) {
    return name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("");
  }

  return (
    <Sheet open={open} onClose={onClose} title="Bind persona">
      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "var(--font-sans)" }}>
        <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
          Only ACTIVE personas are shown. Draft or retired personas cannot be bound.
        </p>

        {isLoading && (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Loading personas…
          </div>
        )}

        {isError && (
          <div style={{ fontSize: 12, color: "var(--error)" }}>
            Failed to load personas.
          </div>
        )}

        {!isLoading && !isError && activePersonas.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
            No active personas available.
          </div>
        )}

        {/* Persona list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {activePersonas.map((p) => {
            const selected = p.persona_id === currentPersonaId;
            return (
              <button
                key={p.persona_id}
                onClick={() => {
                  onSelect(p.persona_id);
                  onClose();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  minHeight: "var(--tap-min)",
                  borderRadius: 8,
                  border: selected
                    ? "1px solid var(--accent)"
                    : "1px solid var(--border)",
                  background: selected
                    ? "var(--accent-subtle)"
                    : "var(--bg-tertiary)",
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: selected ? "var(--accent)" : "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 700,
                    fontSize: 12,
                    color: selected ? "white" : "var(--text-secondary)",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(p.display_name)}
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-primary)",
                    }}
                  >
                    {p.display_name}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {p.role} ·{" "}
                    {p.has_voice_ref ? (
                      <span style={{ color: "var(--success)" }}>voice ✓</span>
                    ) : (
                      <span style={{ color: "var(--warning)" }}>no voice</span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {p.persona_id}
                  </span>
                </div>
                {selected && (
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--accent)",
                      fontWeight: 700,
                    }}
                  >
                    bound
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Unbind option */}
        {currentPersonaId && (
          <button
            onClick={() => {
              onSelect(null);
              onClose();
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid rgba(239,68,68,0.35)",
              background: "transparent",
              color: "var(--error)",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              alignSelf: "flex-start",
            }}
          >
            Unbind persona
          </button>
        )}
      </div>
    </Sheet>
  );
}
